import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Share2, Tv } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useDialog } from '../hooks/useDialog';
import toast from 'react-hot-toast';

// Invite guests: a QR code any phone camera can scan straight into the
// session, plus the link and session ID for manual sharing.
const InviteModal = ({ session, onClose }) => {
  const [copied, setCopied] = useState(false);
  const dialogRef = useDialog(onClose);
  const joinUrl = `${window.location.origin}/session/${session.id}`;

  const copyLink = async () => {
    try {
      if (navigator.share) {
        // Native share sheet on mobile — the friendliest path
        await navigator.share({
          title: `Join ${session.name} on JukeHive`,
          text: `Join my music session "${session.name}"!`,
          url: joinUrl
        });
        return;
      }
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      if (err?.name === 'AbortError') return; // user dismissed share sheet
      // Clipboard fallback for older browsers
      try {
        const input = document.createElement('input');
        input.value = joinUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        setCopied(true);
        toast.success('Link copied!');
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error('Could not copy the link');
      }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Invite guests"
      >
        <motion.div
          ref={dialogRef}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-gray-800 rounded-lg w-full max-w-sm p-6 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center">
              <Share2 className="w-5 h-5 mr-2" />
              Invite Guests
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Close invite dialog"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* QR — white padding so any camera app reads it in dim party light */}
          <div className="bg-white rounded-xl p-4 inline-block mb-4">
            <QRCodeSVG value={joinUrl} size={200} level="M" />
          </div>

          <p className="text-gray-300 text-sm mb-1">
            Guests can point their camera at the code to join
          </p>
          <p className="text-gray-400 text-xs mb-4">
            No app needed — it opens right in the browser
          </p>

          <button
            onClick={copyLink}
            className="btn-primary w-full flex items-center justify-center mb-3"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Share Link
              </>
            )}
          </button>

          <div className="bg-gray-700 rounded-lg px-3 py-2">
            <p className="text-xs text-gray-400 mb-0.5">Or share the session ID</p>
            <p className="text-white font-mono text-sm select-all">{session.id}</p>
          </div>

          {/* Only the host receives joinPin in the session payload */}
          {session.joinPin && (
            <div className="bg-purple-600/15 border border-purple-500/30 rounded-lg px-3 py-2 mt-3">
              <p className="text-xs text-gray-300 mb-0.5">
                Party PIN — lets guests in when their location won't cooperate
              </p>
              <p className="text-white font-mono text-2xl tracking-[0.4em] select-all">
                {session.joinPin}
              </p>
            </div>
          )}

          {/* The party's public stage — open on a TV or laptop */}
          {session.joinPin && (
            <a
              href={`/tv/${session.id}?pin=${session.joinPin}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full flex items-center justify-center px-4 py-2.5 rounded-lg bg-gray-700 text-gray-200 text-sm font-medium hover:bg-gray-600 transition-colors"
            >
              <Tv className="w-4 h-4 mr-2" />
              Open TV display
            </a>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InviteModal;
